import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { apiPublico } from "../../services/api";
import Logo from "../../assets/logo_pagina_login.png";
import * as S from "./styled";

/*
  Página pública onde o responsável envia o contrato assinado em PDF.

  Chega por link de email: /assinar-contrato?token=<64 hex>. O token é a única
  credencial — o responsável não tem conta na plataforma.

  Usa `apiPublico` (e não o `api` padrão) de propósito: o interceptor do api desloga
  e redireciona para /login em 404/401/403, e aqui o 404 é resposta legítima para
  "link inválido ou expirado", que esta tela precisa mostrar.

  Backend: GET /assinar-contrato, POST /assinar-contrato, POST /reenviar-contrato.
*/

const TOKEN_REGEX = /^[a-f0-9]{64}$/; // mesmo formato validado no backend
const TAMANHO_MAXIMO_MB = 10;
const TAMANHO_MAXIMO_BYTES = TAMANHO_MAXIMO_MB * 1024 * 1024;

function formatarTamanho(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function IconeUpload({ ok }) {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {ok ? (
        <path
          d="M20 6L9 17l-5-5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <>
          <path
            d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 16v2.5A1.5 1.5 0 005.5 20h13a1.5 1.5 0 001.5-1.5V16"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )}
    </svg>
  );
}

function AssinarContrato() {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token")?.trim() || "";

  const [carregando, setCarregando] = useState(true);
  const [dados, setDados] = useState(null);
  const [erroFatal, setErroFatal] = useState(null);

  const [arquivo, setArquivo] = useState(null);
  const [arrastando, setArrastando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [concluido, setConcluido] = useState(null);

  const inputRef = useRef(null);

  const carregarContrato = useCallback(async () => {
    // Token malformado nem chega a bater na API: a resposta seria a mesma.
    if (!TOKEN_REGEX.test(token)) {
      setErroFatal(
        "Este link de assinatura é inválido. Confira se copiou o endereço completo do e-mail."
      );
      setCarregando(false);
      return;
    }

    try {
      const { data } = await apiPublico.get("/assinar-contrato", {
        params: { token },
      });

      setDados(data.data);
    } catch (err) {
      const status = err.response?.status;

      setErroFatal(
        status === 404
          ? "Este link de assinatura é inválido ou já expirou. Se o contrato já foi aprovado, o link deixa de funcionar."
          : status === 400
          ? "Este link de assinatura é inválido. Confira se copiou o endereço completo do e-mail."
          : "Não conseguimos carregar os dados do contrato agora. Tente novamente em alguns minutos."
      );
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    carregarContrato();
  }, [carregarContrato]);

  function selecionarArquivo(selecionado) {
    setAviso(null);

    if (!selecionado) return;

    /*
      Validação no cliente é só conveniência: o backend confere de novo, inclusive
      a assinatura "%PDF-" do conteúdo, porque o mimetype do multipart vem do cliente.
    */
    const ehPdf =
      selecionado.type === "application/pdf" ||
      selecionado.name.toLowerCase().endsWith(".pdf");

    if (!ehPdf) {
      setArquivo(null);
      setAviso("O contrato precisa estar em PDF. Selecione um arquivo .pdf.");
      return;
    }

    if (selecionado.size > TAMANHO_MAXIMO_BYTES) {
      setArquivo(null);
      setAviso(
        `O arquivo tem ${formatarTamanho(selecionado.size)} e o limite é ${TAMANHO_MAXIMO_MB}MB.`
      );
      return;
    }

    if (selecionado.size === 0) {
      setArquivo(null);
      setAviso("O arquivo selecionado está vazio.");
      return;
    }

    setArquivo(selecionado);
  }

  function aoSoltar(e) {
    e.preventDefault();
    setArrastando(false);
    selecionarArquivo(e.dataTransfer.files?.[0]);
  }

  async function enviar(e) {
    e.preventDefault();

    if (!arquivo) {
      setAviso("Selecione o contrato assinado em PDF.");
      return;
    }

    setEnviando(true);
    setAviso(null);

    // Contrato REJEITADO usa a rota de reenvio; PENDENTE, a de primeiro envio.
    const rota =
      dados.contrato.acao === "reenviar" ? "/reenviar-contrato" : "/assinar-contrato";

    try {
      const form = new FormData();
      form.append("token", token);
      form.append("contrato", arquivo);

      const { data } = await apiPublico.post(rota, form);

      toast.success(data.message);
      setConcluido(data.message);
    } catch (err) {
      const status = err.response?.status;
      const mensagemApi = err.response?.data?.message;

      if (status === 409) {
        /*
          O contrato saiu do estado esperado (outra aba enviou, ou a coordenação
          avaliou nesse meio tempo). Recarrega para a tela refletir o estado real.
        */
        toast.error(mensagemApi || "Este contrato já foi enviado.");
        setArquivo(null);
        setCarregando(true);
        await carregarContrato();
        return;
      }

      // 400 traz mensagem pronta para o usuário (ex.: formato recusado).
      const mensagem =
        status === 400
          ? mensagemApi || "Arquivo inválido. Envie o contrato em PDF, com até 10MB."
          : status === 404
          ? "Este link de assinatura é inválido ou já expirou."
          : status === 502
          ? "Não conseguimos armazenar o arquivo agora. Tente novamente em alguns minutos."
          : "Não foi possível enviar o contrato. Tente novamente.";

      toast.error(mensagem);
      setAviso(mensagem);
    } finally {
      setEnviando(false);
    }
  }

  function renderConteudo() {
    if (erroFatal) {
      return (
        <S.Painel $tom="erro">
          <div className="icone">🔗</div>
          <strong>Link indisponível</strong>
          <p>{erroFatal}</p>
        </S.Painel>
      );
    }

    if (concluido) {
      return (
        <S.Painel $tom="sucesso">
          <div className="icone">✅</div>
          <strong>Contrato recebido!</strong>
          <p>{concluido}</p>
        </S.Painel>
      );
    }

    const { aluno, responsavel, contrato } = dados;

    const identificacao = (
      <S.Identificacao>
        <dl>
          <dt>Aluno</dt>
          <dd>{aluno.nome}</dd>
          <dt>Turma</dt>
          <dd>{aluno.turma}</dd>
          <dt>Responsável</dt>
          <dd>{responsavel.nome}</dd>
        </dl>
      </S.Identificacao>
    );

    // acao === null: não há o que enviar (contrato em análise ou já aprovado).
    if (!contrato.acao) {
      const emAnalise = contrato.status_contrato === "ENVIADO";

      return (
        <>
          {identificacao}
          <S.Painel $tom={emAnalise ? "info" : "sucesso"}>
            <div className="icone">{emAnalise ? "⏳" : "✅"}</div>
            <strong>
              {emAnalise ? "Contrato em análise" : "Contrato aprovado"}
            </strong>
            <p>
              {emAnalise
                ? "Já recebemos o contrato assinado e nossa equipe está analisando. Você será avisado por e-mail assim que for aprovado."
                : "O contrato foi aprovado e o acesso do aluno já está liberado. Não é preciso enviar nada."}
            </p>
          </S.Painel>
        </>
      );
    }

    const reenvio = contrato.acao === "reenviar";

    return (
      <>
        {identificacao}

        {reenvio && (
          <S.Painel $tom="erro" style={{ marginBottom: 22 }}>
            <div className="icone">⚠️</div>
            <strong>O contrato anterior foi recusado</strong>
            <p>
              Confira se o documento está assinado e legível e envie novamente.
            </p>
          </S.Painel>
        )}

        <form onSubmit={enviar}>
          <S.Dropzone
            $ativo={arrastando}
            $temArquivo={!!arquivo}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={aoSoltar}
          >
            <IconeUpload ok={!!arquivo} />
            {arquivo ? (
              <>
                <strong>{arquivo.name}</strong>
                <span>
                  {formatarTamanho(arquivo.size)} · clique para trocar o arquivo
                </span>
              </>
            ) : (
              <>
                <strong>Selecione o contrato assinado</strong>
                <span>
                  Clique aqui ou arraste o arquivo · PDF de até {TAMANHO_MAXIMO_MB}MB
                </span>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              disabled={enviando}
              onChange={(e) => selecionarArquivo(e.target.files?.[0])}
            />
          </S.Dropzone>

          <S.Botao type="submit" disabled={enviando || !arquivo}>
            {enviando && <S.Spinner />}
            {enviando ? "Enviando..." : reenvio ? "Reenviar contrato" : "Enviar contrato"}
          </S.Botao>
        </form>

        {aviso && <S.Aviso>{aviso}</S.Aviso>}
      </>
    );
  }

  if (carregando) {
    return (
      <S.Wrapper>
        <S.Carregando>
          <span className="roda" />
          <p>Carregando os dados do contrato...</p>
        </S.Carregando>
      </S.Wrapper>
    );
  }

  const emFormulario = !erroFatal && !concluido && dados?.contrato?.acao;

  return (
    <S.Wrapper>
      <img className="logo" src={Logo} alt="Curso Danielle Velasco" />

      <S.Card>
        <h1>{erroFatal ? "Contrato" : "Envio do contrato"}</h1>
        {emFormulario && (
          <S.Subtitulo>
            Envie o contrato assinado para concluir a matrícula do aluno.
          </S.Subtitulo>
        )}

        {renderConteudo()}
      </S.Card>

      <S.Rodape>
        Em caso de dúvidas, entre em contato com a equipe do Portal Danielle Velasco.
      </S.Rodape>
    </S.Wrapper>
  );
}

export default AssinarContrato;
